import { ActionIcon, Group, Tooltip } from "@mantine/core";
import {
  IconEdit,
  IconStar,
  IconStarFilled,
  IconTrash
} from "@tabler/icons-react";
import * as _ from "lodash-es";
import type { MRT_ColumnDef, MRT_TableOptions } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { apiWithoutCamelization } from "../../../../api";
import { mieterstromInvoicingApiWithoutCamelization } from "../../../../api/subApis/mieterstromInvoicingApi";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { MRT_TableDetailPanelWrapper } from "../../../../mantine/mrt/components/other/MRT_TableDetailPanelWrapper";
import { useToast } from "../../../../mantine/notifications/useToast";
import type { Delivery, Person, Site } from "../../../../utils/backend-types";
import { getErrorText } from "../../../../utils/get-error-text";
import { MultiConfirmationModal } from "../../../BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import type { Contract } from "../../DeliveryOverview.types";
import { getAutomaticExtensionColumn } from "./columns/getAutomaticExtensionColumn";
import { getBeginColumn } from "./columns/getBeginColumn";
import { getDeliveriesColumn } from "./columns/getDeliveriesColumn";
import { getEndColumn } from "./columns/getEndColumn";
import { getIsTemplateColumn } from "./columns/getIsTemplateColumn";
import { getSiteColumn } from "./columns/getSiteColumn";
import { getSuppliedColumn } from "./columns/getSuppliedColumn";
import { getSupplierColumn } from "./columns/getSupplierColumn";
import { ContractDeliverySubComponent } from "./ContractDeliverySubComponent/ContractDeliverySubComponent";
import "./ContractDeliveryTable.scss";
import { getSiteNameFromContractAndDeliveries } from "./utils/getSiteNameFromContractAndDeliveries";

type TableData = {
  id: string;
  name: string;
};

interface ContractDeliveryTableProps {
  contracts: Array<Contract>;
  deliveries: Array<Delivery>;
  persons: Array<Person>;
  onDeliveriesUpdated: () => void;
  onContractsUpdated: () => void;
  onDataDeleted: (ids: Array<string>) => void;
  projectId: string;
  sites: Array<Site>;
}

function ContractDeliveryTable({
  contracts,
  deliveries,
  persons,
  onDeliveriesUpdated,
  onContractsUpdated,
  onDataDeleted,
  projectId,
  sites
}: ContractDeliveryTableProps) {
  const { showErrorToast } = useToast();
  const navigate = useNavigate();
  const [dataToDelete, setDataToDelete] = useState<TableData | undefined>(
    undefined
  );
  const dataToDeleteModalIsOpen = dataToDelete !== undefined;
  const toggleDeleteDataModal = () => setDataToDelete(undefined);

  const handleClickMarkContractAsTemplate = useCallback(
    async (contract?: Contract) => {
      if (contract) {
        const newContract = { ...contract };
        newContract.is_template = !contract.is_template;
        try {
          await apiWithoutCamelization.put(
            mieterstromInvoicingApiWithoutCamelization.contract.url(
              contract.id
            ),
            newContract
          );
        } catch (error) {
          showErrorToast(error);
        } finally {
          onContractsUpdated();
        }
      }
    },
    [onContractsUpdated, showErrorToast]
  );

  function handleClickDeleteContract(contract?: Contract) {
    if (contract) {
      setDataToDelete({ id: contract.id, name: contract.name });
    }
  }

  function handleDeleteSelectedData(ids: Array<string>): Promise<void> {
    const deletePromises = ids.map((id) =>
      apiWithoutCamelization.delete<void>(
        mieterstromInvoicingApiWithoutCamelization.contract.url(id.toString())
      )
    );

    return Promise.all(deletePromises)
      .then(() => {
        onDataDeleted(ids);
      })
      .catch((error) => {
        const errorMessage = getErrorText(error, {
          403: "Es konnten nicht alle Verträge gelöscht werden. Verträge, für welche bereits Rechnungen erstellt wurden, dürfen nicht gelöscht werden."
        });
        showErrorToast(errorMessage);
      })
      .finally(() => {
        setDataToDelete(undefined);
      });
  }

  const defaultOptions = getDefaultMRTOptions<Contract>({
    emptyRowsFallbackText: "Keine Verträge vorhanden",
    initialState: {
      pagination: { pageIndex: 0, pageSize: 100 },
      showGlobalFilter: true
    },
    hideBorder: true
  });

  const customTableOptions: Omit<
    MRT_TableOptions<Contract>,
    "columns" | "data"
  > = {
    enablePagination: contracts.length > 100,

    filterFns: {
      filterNameSiteSupplierSupplied: (row, _, filterValue) => {
        const lowerFilterValue = filterValue.toLowerCase();
        const name = row.getValue<Contract["name"]>("name");
        const site = getSiteNameFromContractAndDeliveries(
          row.original,
          deliveries,
          sites
        );
        const supplier = persons.find(
          (person) => person.id === row.original.supplier
        )?.name;
        const supplied = persons.find(
          (person) => person.id === row.original.supplied
        )?.name;

        const containsSearchTerm = (value: string | undefined) =>
          value?.toLowerCase().includes(lowerFilterValue);

        return (
          containsSearchTerm(name) ||
          containsSearchTerm(site) ||
          containsSearchTerm(supplier) ||
          containsSearchTerm(supplied) ||
          false
        );
      }
    },
    globalFilterFn: "filterNameSiteSupplierSupplied",

    columnFilterDisplayMode: "popover",

    enableRowActions: true,
    renderRowActions: ({ row }) => {
      const hasInvoices = row.original.invoice_count > 0;

      return (
        <Group wrap="nowrap">
          <Tooltip label="Vertrag bearbeiten">
            <ActionIcon
              color="secondary"
              component={Link}
              size="xs"
              to={`./${row.original.id}/allgemein`}
              variant="transparent"
            >
              <IconEdit />
            </ActionIcon>
          </Tooltip>

          <Tooltip
            label={
              row.original.is_template
                ? "„Referenzvertrag“-Markierung entfernen"
                : "Als Referenzvertrag für neue Verträge markieren"
            }
          >
            <ActionIcon
              color="secondary"
              size="xs"
              variant="transparent"
              onClick={() => {
                handleClickMarkContractAsTemplate(
                  contracts.find((contract) => contract.id === row.original.id)
                );
              }}
            >
              {row.original.is_template ? <IconStarFilled /> : <IconStar />}
            </ActionIcon>
          </Tooltip>

          <Tooltip
            label={
              hasInvoices
                ? "Verträge, für die bereits Rechnungen erstellt wurden, können nicht gelöscht werden."
                : "Vertrag löschen"
            }
          >
            <ActionIcon
              className="delete-action-icon"
              color="error"
              disabled={hasInvoices}
              size="xs"
              variant="transparent"
              onClick={() => {
                handleClickDeleteContract(row.original);
              }}
            >
              <IconTrash />
            </ActionIcon>
          </Tooltip>
        </Group>
      );
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        header: "",
        size: 120
      }
    },

    ...getMRTTopToolbarCustomActionsRenderer<Contract>({
      create: {
        text: "Vertrag hinzufügen",
        onClick: () => navigate("./neu")
      },
      delete: { hide: true }
    }),
    enableRowSelection: false,

    renderDetailPanel: ({ row }) => {
      if (row.id === "mrt-row-empty") {
        return null;
      }

      const contractDeliveries = deliveries.filter(
        (delivery) => delivery.contract === row.original.id
      );

      return (
        <MRT_TableDetailPanelWrapper>
          <ContractDeliverySubComponent
            contract={row.original}
            deliveries={contractDeliveries}
            persons={persons}
            projectId={projectId}
            sites={sites}
            onDeliveriesUpdated={onDeliveriesUpdated}
          />
        </MRT_TableDetailPanelWrapper>
      );
    },

    enableColumnResizing: true
  };

  const tableOptions: Omit<
    MRT_TableOptions<Contract>,
    "columns" | "data"
  > = _.merge({}, defaultOptions, customTableOptions);

  const columnsMRT: Array<MRT_ColumnDef<Contract>> = useMemo(() => {
    const cols = [
      {
        accessorKey: "name",
        header: "Vertragsname",
        enableColumnFilter: false
      },
      getBeginColumn(contracts),
      getEndColumn(contracts),
      getSiteColumn(contracts, deliveries, sites),
      getSupplierColumn(contracts, persons),
      getSuppliedColumn(contracts, persons),
      getAutomaticExtensionColumn(),
      getDeliveriesColumn(deliveries),
      getIsTemplateColumn()
    ];

    return cols;
  }, [contracts, deliveries, sites, persons]);

  const table = useMantineReactTable({
    ...tableOptions,
    columns: columnsMRT,
    data: contracts
  });

  return (
    <div className="ContractDeliveryTable">
      <MantineReactTable table={table} />
      <MultiConfirmationModal
        actionName="löschen"
        actionObjects={dataToDelete?.name ? [dataToDelete.name] : []}
        confirmationText={`Möchten Sie den folgenden Vertrag löschen? Dieser Schritt kann nicht rückgängig gemacht werden.`}
        isModalOpen={dataToDeleteModalIsOpen}
        objectName={"Vertrag"}
        toggleModal={toggleDeleteDataModal}
        onAction={() =>
          handleDeleteSelectedData(dataToDelete?.id ? [dataToDelete.id] : [])
        }
      />
    </div>
  );
}

export { ContractDeliveryTable };
